import type { ChangeEvent } from 'react';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputBase, makeStyles } from '@material-ui/core';
import type { InputBaseProps, Theme } from '@material-ui/core';

import { Block } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { stripHtml } from '@klp/helpers';

interface IProps extends Omit<InputBaseProps, 'onChange'> {
    searchHandler: (search: string) => void;
    searchOnType?: boolean;
}

export const highlightSearch = (
    value: string | undefined,
    search: string,
): string => {
    return stripHtml(value || '').replace(
        new RegExp(search, 'gi'),
        (str: string) => (search.length > 0 ? `<b>${str}</b>` : str),
    );
};

// tslint:disable-next-line: whitespace
export const filterSearchResult = <T,>(
    source: Array<T>,
    search: string,
    fields: Array<string>,
) => matchSearchPhrase<T>(source, search, fields);

// tslint:disable-next-line: whitespace
export const matchSearchPhrase = <T,>(
    source: Array<T>,
    search: string,
    fields: Array<string>,
) => {
    // Handle undefined or null source
    if (!source || !Array.isArray(source)) {
        return [];
    }

    return source.filter((row) =>
        fields.some((field) => {
            return row[field]
                ? stripHtml(row[field].toString().toLowerCase()).includes(
                      search.toLowerCase(),
                  )
                : false;
        }),
    );
};

const useStyles = makeStyles((theme: Theme) => ({
    placeholder: {
        border: '1px solid transparent',
        borderColor: theme.palette.primary.medium,
        borderRadius: 2,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        display: 'flex',
        alignItems: 'center',
        '& > *': {
            display: 'flex',
            '&:first-child': {
                flex: '1 1 auto',
            },
            '&:last-child': {
                cursor: 'pointer',
                '&:hover svg': {
                    fill: theme.palette.secondary.main,
                },
            },
        },
    },
}));

export const Search: React.FC<IProps> = ({
    searchHandler,
    placeholder,
    searchOnType,
    ...rest
}) => {
    const classes = useStyles();
    const searchRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const [search, setSearch] = useState('');

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearch(value);

        if (searchOnType) {
            searchHandler(value);
        }
    };

    const handleSearch = () => {
        if (!searchRef.current) return;
        const value = searchRef.current.value;
        setSearch(value);
        searchHandler(value);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            searchHandler(search);
        }
    };

    return (
        <Block className={classes.placeholder} marginBottom="compact">
            <InputBase
                {...rest}
                inputRef={searchRef}
                className="Search__Search"
                placeholder={t(placeholder ?? '')}
                onChange={handleChange}
                autoFocus={true}
                onKeyPress={handleKeyPress}
            />
            <Illustration icon={IconName.SEARCH} onClick={handleSearch} />
        </Block>
    );
};
